import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatMoney, formatNum, can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, Layers, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", sku: "", description: "", selling_price: 0, composition: [] };
const uid = () => Math.random().toString(36).slice(2);

export default function Products() {
  const { user } = useAuth();
  const editable = can(user.role, "products");
  const [items, setItems] = useState([]);
  const [raws, setRaws] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const base = user.organization?.base_currency || "NGN";

  const load = useCallback(async () => {
    const [p, r] = await Promise.all([api.get("/products"), api.get("/raw-materials")]);
    setItems(p.data); setRaws(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rawName = (id) => raws.find((r) => r.id === id)?.name || "Unknown";
  const rawUnit = (id) => raws.find((r) => r.id === id)?.unit || "";

  const addComp = () => setForm((f) => ({ ...f, composition: [...f.composition, { raw_material_id: raws[0]?.id || "", quantity: 1, _key: uid() }] }));
  const setComp = (i, k, v) => setForm((f) => { const c = [...f.composition]; c[i] = { ...c[i], [k]: k === "quantity" ? parseFloat(v || 0) : v }; return { ...f, composition: c }; });
  const rmComp = (i) => setForm((f) => ({ ...f, composition: f.composition.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, composition: form.composition.map(({ raw_material_id, quantity }) => ({ raw_material_id, quantity })) };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete product definition?")) return; await api.delete(`/products/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="products-page">
      <PageHeader title="Products (BoM)" subtitle="Define finished products & their raw-material composition"
        action={editable && <Button className="rounded-none gap-2" data-testid="create-product-btn" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}><Plus className="w-4 h-4" /> New Product</Button>} />

      {items.length === 0 ? (
        <EmptyState title="No products defined" hint="Create a product and specify which raw materials (and quantities) make one unit. You must define a product before producing its inventory."
          action={editable && <Button className="rounded-none gap-2" onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}><Plus className="w-4 h-4" />New Product</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-border bg-card p-5 flex flex-col" data-testid="product-card">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center"><Layers className="w-5 h-5" /></div>
                <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
              </div>
              <h3 className="font-display text-lg font-bold mt-3">{p.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2">{p.description || "No description"}</p>
              <div className="flex items-center justify-between mt-3 text-sm">
                <span className="text-muted-foreground">Price</span>
                <span className="font-mono font-bold">{formatMoney(p.selling_price, base)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">In stock</span>
                <span className="font-mono font-bold">{formatNum(p.total_stock, 0)} units</span>
              </div>
              <div className="mt-3 border-t border-border pt-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Composition / unit</div>
                <div className="space-y-1">
                  {p.composition.length === 0 && <div className="text-xs text-muted-foreground">No raw materials set</div>}
                  {p.composition.map((c, i) => (
                    <div key={`${c.raw_material_id}-${i}`} className="flex justify-between text-xs font-mono">
                      <span className="truncate">{rawName(c.raw_material_id)}</span>
                      <span>{formatNum(c.quantity)} {rawUnit(c.raw_material_id)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {editable && (
                <div className="flex gap-1 mt-4">
                  <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => { setForm({ ...p, composition: (p.composition || []).map((c) => ({ ...c, _key: uid() })) }); setEditing(p.id); setOpen(true); }} data-testid="edit-product-btn"><Pencil className="w-3.5 h-3.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="rounded-none text-destructive gap-1" onClick={() => del(p.id)} data-testid="delete-product-btn"><Trash2 className="w-3.5 h-3.5" /> Delete</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Product</DialogTitle><DialogDescription>Define a finished product and the raw materials that make one unit.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="product-name-input" /></div>
              <div><Label>SKU</Label><Input className="rounded-none mt-1" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} required data-testid="product-sku-input" /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea className="rounded-none mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
              <div><Label>Selling Price ({base})</Label><Input type="number" step="any" className="rounded-none mt-1" value={form.selling_price} onChange={(e) => setForm((f) => ({ ...f, selling_price: parseFloat(e.target.value || 0) }))} data-testid="product-price-input" /></div>
            </div>

            <div className="border border-border p-3 bg-secondary/40">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider">Bill of Materials (per unit)</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-none gap-1" onClick={addComp} disabled={raws.length === 0} data-testid="add-composition-btn"><Plus className="w-3.5 h-3.5" /> Add material</Button>
              </div>
              {raws.length === 0 && <p className="text-xs text-destructive">Create raw materials first.</p>}
              <div className="space-y-2">
                {form.composition.map((c, i) => (
                  <div key={c._key || i} className="flex flex-wrap gap-2 items-center" data-testid="composition-row">
                    <Select value={c.raw_material_id} onValueChange={(v) => setComp(i, "raw_material_id", v)}>
                      <SelectTrigger className="rounded-none flex-1 min-w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{raws.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.unit})</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" step="any" className="rounded-none w-28" value={c.quantity} onChange={(e) => setComp(i, "quantity", e.target.value)} placeholder="Qty" />
                    <Button type="button" size="icon" variant="ghost" className="h-9 w-9 rounded-none text-destructive" onClick={() => rmComp(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {form.composition.length === 0 && <p className="text-xs text-muted-foreground">No materials added yet.</p>}
              </div>
            </div>

            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-product-btn">Save Product</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
